import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { SERVICES } from "../data/content";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((item) => item.slug === slug);

  if (!service) return <Navigate to="/services" replace />;
  const Icon = service.icon;
  const related = SERVICES.filter((item) => item.slug !== slug).slice(0, 3);

  return (
    <main data-testid={`service-detail-${service.slug}`} className="bg-[#070C14] text-white">
      <PageHero
        testId="service-detail-hero"
        eyebrow="Service practice"
        title={service.title}
        description={service.summary}
        image={service.image}
        imageAlt={service.title}
      />

      <section data-testid="service-detail-content" className="mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-8 lg:grid-cols-[.75fr_1.25fr] lg:px-10">
        <Reveal>
          <Link to="/services" data-testid="back-to-services-link" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-blue-300 transition-colors hover:text-amber-300">
            <ArrowLeft size={16} /> All services
          </Link>
          <div className="mt-10 rounded-3xl border border-white/10 bg-[#0D131F] p-8">
            <Icon className="text-amber-400" size={32} />
            <h2 className="mt-8 font-heading text-3xl font-semibold">Delivery markers</h2>
            <div className="mt-6 space-y-4">
              {service.highlights.map((highlight) => (
                <p key={highlight} className="flex gap-3 text-sm leading-6 text-slate-300" data-testid={`highlight-${highlight.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <CheckCircle2 className="mt-0.5 shrink-0 text-blue-400" size={18} /> {highlight}
                </p>
              ))}
            </div>
          </div>
        </Reveal>
        <div className="space-y-6">
          {service.body.map((paragraph, index) => (
            <Reveal key={paragraph.slice(0, 24)} delay={index * 0.08} className="rounded-3xl border border-white/10 bg-white/[0.035] p-8 text-base leading-9 text-slate-300 sm:p-10">
              {paragraph}
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="related-services-section" className="mx-auto max-w-7xl px-4 pb-24 sm:px-8 lg:px-10">
        <Reveal className="mb-10 flex items-end justify-between">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.32em] text-blue-400">Connected practices</p>
            <h2 className="mt-4 font-heading text-3xl font-bold">Continue through the value web</h2>
          </div>
        </Reveal>
        <div className="grid gap-5 md:grid-cols-3">
          {related.map((item, index) => (
            <Reveal key={item.slug} delay={index * 0.06}>
              <Link to={`/services/${item.slug}`} data-testid={`related-service-${item.slug}`} className="group flex min-h-56 flex-col justify-between rounded-3xl border border-white/10 bg-[#0D131F] p-7 transition-all duration-500 hover:-translate-y-2 hover:border-amber-400/40">
                <item.icon className="text-blue-400" size={26} />
                <div>
                  <h3 className="font-heading text-2xl font-semibold">{item.title}</h3>
                  <p className="mt-3 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-amber-300">Open <ArrowUpRight size={15} /></p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}
